import { Button } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { shallowEqual, useSelector } from 'react-redux'
import useListIndexed from 'utils/customHook/useListIndexed'
import { intersectionWith, isNil } from 'lodash'
import SimpleSelectionTable, { QualitometerFilterField } from 'components/datatable/SimpleSelectionTable'
import { normalizeString } from 'utils/StringUtil'
import { hybridSearch } from 'utils/SearchUtil'
import StepperDialog from 'components/modal/StepperDialog'
import { CardTable, TableSelect } from 'components/datatable/NewTable'
import useSandreList from 'utils/customHook/useSandreList'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import useListGrouped from 'utils/customHook/useListGrouped'
import { QualityCampaignContext } from 'quality/components/campaign/QualityCampaign'
import { negIdGenerator } from 'campaign/utils/CampaignUtils'
import { SHOW_INSEE_CODE, SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'

const StepSelectStations = ({
    selectedStations = [],
    setSelectedStations = () => {},
}) => {
    const {
        cities,
        networkLinks,
        qualitometers,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        networkLinks: store.QualityReducer.networkLinks,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const indexedCities = useListIndexed(cities, 'id')

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const formattedStations = useMemo(() => {
        return qualitometers.map(q => {
            const city = indexedCities[q.townCode]
            return {
                id: q.id,
                name: `${q.code ? `[${q.code}] ` : ''}${q.name}`,
                city: (showInseeCode ? city?.labelWithCode : city?.name) || '',
                townCode: q.townCode,
                networks: networkLinks.filter(n => n.idStation === q.id),
                _normalizedSearchString: [q.code, q.name, q.townCode, city?.name].filter(l => !!l).map(s => normalizeString(s)),
            }
        })
    }, [qualitometers, indexedCities, networkLinks, showInseeCode])

    const onChange = (list = []) => {
        setSelectedStations(prevSS => {
            return list.map(id => {
                const prev = prevSS.find(cs => id === cs.stationId)
                return prev ?? { stationId: id, id: negIdGenerator.next().value }
            })
        })
    }

    return (
        <SimpleSelectionTable
            onChange={onChange}

            listData={formattedStations}
            selectedList={selectedStations.map(s => s.stationId)}

            listHeaders={['name', 'city']}
            listTitle={i18n.nonSelectedStations}

            selectedListTitle={i18n.selectedStations}
            maxHeightTable='43vh'

            filterField={QualitometerFilterField}
            filterFunction={(list, filterValues) => {
                const { searchValue, city, network, filter = -1, resultFilter } = filterValues
                const filteredList = list.filter(e => !isNil(e))
                const availableStations = filter !== -1 ? intersectionWith(filteredList, resultFilter, (elem, res) => elem.id === res.id) : filteredList
                const filterSearch = hybridSearch(availableStations, normalizeString(searchValue))
                const filterNetwork = !isNil(network) ? filterSearch.filter(s => s.networks.some(n => n.idNetwork === network)) : filterSearch
                return !isNil(city) ? filterNetwork.filter(p => p.townCode === city) : filterNetwork
            }}
        />
    )
}

StepSelectStations.propTypes = {
    selectedStations: PropTypes.arrayOf(PropTypes.shape({
        stationId: PropTypes.number,
    })),
    setSelectedStations: PropTypes.func,
}

const StepUpdateStations = ({
    selectedStations = [],
    setSelectedStations = () => {},
}) => {
    const {
        cities,
        qualitometers,
        allPoints,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        qualitometers: store.QualityReducer.qualitometersLight,
        allPoints: store.QualityReducer.allPoints,
    }), shallowEqual)
    const indexedCities = useListIndexed(cities, 'id')
    const indexedQualitometers = useListIndexed(qualitometers, 'id')
    const groupedSamplePoints = useListGrouped(allPoints, 'idQualitometer')
    const codificationsList = useSandreList('QUALITOMETRES.NOMENCLATURES')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)
    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const formattedStations = selectedStations.map((ss, index) => {
        const qualito = indexedQualitometers[ss.stationId]
        const city = indexedCities[qualito?.townCode]
        return {
            code: qualito?.code,
            name: qualito?.name,
            city: (showInseeCode ? city?.labelWithCode : city?.name) || '',
            codification: (
                <TableSelect
                    value={ss.codification}
                    items={codificationsList}
                    onChange={codification => setSelectedStations(prevSS => prevSS.toSpliced(index, 1, { ...prevSS[index], codification }))}
                    keyValue='code'
                    keyLabel='name'
                />
            ),
            samplePoint: (
                <TableSelect
                    value={ss.samplePoint}
                    items={groupedSamplePoints[ss.stationId]}
                    onChange={samplePoint => setSelectedStations(prevSS => prevSS.toSpliced(index, 1, { ...prevSS[index], samplePoint }))}
                    keyValue='idPoint'
                    keyLabel={showSandreCode ? 'displayLabel' : 'name'}
                />
            ),
            index,
        }
    })
    return (
        <CardTable
            title={i18n.qualitometers}
            headers={['code', 'name', 'city', 'samplePoint', 'codification']}
            rows={formattedStations}
            lineActions={[{
                icon: 'close',
                onClick: ({ index }) => setSelectedStations(prevSS => prevSS.toSpliced(index, 1)),
            }, {
                icon: 'add_to_photos', // duplicate
                onClick: ({ index }) => setSelectedStations(prevSS => prevSS.toSpliced(index, 0, { ...prevSS[index] })),
            }]}
            rowsPerPageOptions={nbPerPageLabel}
            maxHeight={'55vh'}
        />
    )
}

StepUpdateStations.propTypes = {
    selectedStations: PropTypes.arrayOf(PropTypes.shape({
        stationId: PropTypes.number,
        codification: PropTypes.number,
        samplePoint: PropTypes.number,
    })),
    setSelectedStations: PropTypes.func,
}

const STEP = {
    SELECTION: 0,
    UPDATE: 1,
}

const PopinQualityCampaignStations = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
}) => {
    const { campaignStations } = useContext(QualityCampaignContext)
    const [selectedStations, setSelectedStations] = useState(campaignStations)

    useEffect(() => {
        setSelectedStations(campaignStations)
    }, [campaignStations])

    return (
        <StepperDialog
            steps={[
                {
                    label: i18n.qualitometersSelection,
                    constant: STEP.SELECTION,
                    nextAvailable: selectedStations.length > 0,
                },
                {
                    label: i18n.additionalInformation,
                    constant: STEP.UPDATE,
                },
            ]}
            open={isOpen}
            title={i18n.stations}
            closeDialog={onClose}
            renderSaveButton={step => (step === STEP.UPDATE) && (
                <Button
                    onClick={() => onValidate(selectedStations)}
                    variant='contained'
                >
                    {i18n.validate}
                </Button>
            )}
            sx={{ minWidth: '80vw' }}
        >
            {step => (
                <>
                    {step === STEP.SELECTION && (
                        <StepSelectStations
                            selectedStations={selectedStations}
                            setSelectedStations={setSelectedStations}
                        />
                    )}
                    {step === STEP.UPDATE && (
                        <StepUpdateStations
                            selectedStations={selectedStations}
                            setSelectedStations={setSelectedStations}
                        />
                    )}
                </>
            )}
        </StepperDialog>
    )
}

PopinQualityCampaignStations.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

export default PopinQualityCampaignStations